<template>
  <div class="image-annotation-container">
    <!-- Toolbar -->
    <div class="annotation-toolbar">
      <div class="tool-group">
        <button 
          v-for="tool in tools" 
          :key="tool.id"
          @click="setActiveTool(tool.id)"
          :class="['tool-btn', { active: activeTool === tool.id }]"
          :title="tool.name"
        >
          <v-icon :size="18">{{ tool.icon }}</v-icon>
          <span>{{ tool.name }}</span>
        </button>
      </div>
      
      <div class="tool-group">
        <!-- Color picker for highlights and drawings -->
        <div class="color-picker" v-if="needsColor">
          <input 
            type="color" 
            v-model="activeColor" 
            @change="updateColor"
            title="Choose color"
          />
        </div>
        
        <!-- Brush size for free draw -->
        <div class="brush-size" v-if="activeTool === 'draw'">
          <input 
            type="range" 
            v-model="brushSize" 
            min="2" 
            max="10" 
            @input="updateBrushSize"
            title="Brush size"
          />
          <span>{{ brushSize }}px</span>
        </div>
      </div>
      
      <div class="tool-group">
        <button @click="undo" :disabled="!canUndo" class="tool-btn" title="Undo">
          <v-icon :size="18">mdi-undo</v-icon>
        </button>
        <button @click="clearAll" class="tool-btn danger" title="Clear all annotations">
          <v-icon :size="18">mdi-delete</v-icon>
        </button>
      </div>
      
      <div class="tool-group">
        <button @click="$emit('cancel')" class="tool-btn cancel">
          Cancel
        </button>
        <button @click="saveAnnotatedImage" class="tool-btn save">
          Use This Image
        </button>
      </div>
    </div>

    <!-- Canvas Container -->
    <div class="canvas-container" ref="canvasContainer">
      <div class="canvas-wrapper" ref="canvasWrapper">
        <canvas 
          ref="canvas"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="stopDrawing"
          @touchstart="handleTouch"
          @touchmove="handleTouch"
          @touchend="stopDrawing"
          :style="{ cursor: currentCursor }"
        ></canvas>
        
        <!-- Blur overlay for hiding sensitive info -->
        <div 
          v-for="(blur, index) in blurAreas" 
          :key="'blur-' + index"
          class="blur-area"
          :style="getBlurStyle(blur)"
        ></div>
      </div>
    </div>

    <!-- Instructions -->
    <div class="instructions">
      <div class="instruction-item" v-if="activeTool === 'highlight'">
        <v-icon size="16">mdi-information</v-icon>
        <span>Click and drag to highlight text in yellow</span>
      </div>
      <div class="instruction-item" v-if="activeTool === 'draw'">
        <v-icon size="16">mdi-information</v-icon>
        <span>Click and drag to draw freely</span>
      </div>
      <div class="instruction-item" v-if="activeTool === 'rectangle'">
        <v-icon size="16">mdi-information</v-icon>
        <span>Click and drag to draw a rectangle</span>
      </div>
      <div class="instruction-item" v-if="activeTool === 'circle'">
        <v-icon size="16">mdi-information</v-icon>
        <span>Click and drag to draw a circle</span>
      </div>
      <div class="instruction-item" v-if="activeTool === 'blur'">
        <v-icon size="16">mdi-information</v-icon>
        <span>Click and drag to hide sensitive information</span>
      </div>
    </div>
  </div>
</template>

<script>
// Using Material Design Icons that are already available in your project

export default {
  name: 'ImageAnnotation',
  
  // No need to import icons - using Material Design Icons
  
  props: {
    imageData: {
      type: String,
      required: true
    },
    existingAnnotations: {
      type: Object,
      default: null
    }
  },
  
  emits: ['save', 'cancel'],
  
  data() {
    return {
      activeTool: 'highlight',
      activeColor: '#ffff00',
      brushSize: 4,
      isDrawing: false,
      startX: 0,
      startY: 0,
      
      // Canvas and context
      canvas: null,
      ctx: null,
      baseImage: null,
      
      // Annotations history
      annotations: [],
      history: [],
      historyIndex: -1,
      
      // Blur areas for hiding sensitive info
      blurAreas: [],
      
      // Tools configuration using Material Design Icons
      tools: [
        { id: 'highlight', name: 'Highlight', icon: 'mdi-marker' },
        { id: 'draw', name: 'Draw', icon: 'mdi-pencil' },
        { id: 'rectangle', name: 'Rectangle', icon: 'mdi-square-outline' },
        { id: 'circle', name: 'Circle', icon: 'mdi-circle-outline' },
        { id: 'blur', name: 'Hide Info', icon: 'mdi-eye-off' }
      ]
    }
  },
  
  computed: {
    needsColor() {
      return ['highlight', 'draw', 'rectangle', 'circle'].includes(this.activeTool)
    },
    
    canUndo() {
      return this.historyIndex >= 0
    },
    
    currentCursor() {
      const cursors = {
        highlight: 'crosshair',
        draw: 'crosshair',
        rectangle: 'crosshair',
        circle: 'crosshair',
        blur: 'crosshair'
      }
      return cursors[this.activeTool] || 'default'
    },
  },
  
  mounted() {
    this.$nextTick(() => {
      this.initializeCanvas()
    })
  },
  
  created() {
    // Load existing annotations if provided
    this.loadExistingAnnotations()
  },
  
  methods: {
    loadExistingAnnotations() {
      if (this.existingAnnotations) {
        console.log('📝 Loading existing annotations...')
        
        // Load existing annotations and blur areas
        this.annotations = this.existingAnnotations.annotations ? 
          JSON.parse(JSON.stringify(this.existingAnnotations.annotations)) : []
        
        this.blurAreas = this.existingAnnotations.blurAreas ? 
          JSON.parse(JSON.stringify(this.existingAnnotations.blurAreas)) : []
        
        console.log(`✅ Loaded ${this.annotations.length} annotations and ${this.blurAreas.length} blur areas`)
      }
    },
    
    async initializeCanvas() {
      // Wait for DOM to be ready
      await this.$nextTick()
      
      // Check if canvas exists
      if (!this.$refs.canvas) {
        console.error('Canvas element not found')
        return
      }
      
      this.canvas = this.$refs.canvas
      this.ctx = this.canvas.getContext('2d')
      
      // Load the base image
      await this.loadBaseImage()
      
      // Set canvas size
      this.resizeCanvas()
      
      // Draw initial image
      this.redrawCanvas()
      
      // Save initial state (this will include existing annotations if any)
      this.saveState()
      
      console.log('🎨 Canvas initialized with existing annotations')
    },
    
    async loadBaseImage() {
      return new Promise((resolve) => {
        this.baseImage = new Image()
        this.baseImage.onload = () => {
          resolve()
        }
        this.baseImage.src = this.imageData
      })
    },
    
    resizeCanvas() {
      const container = this.$refs.canvasContainer
      const containerWidth = container.clientWidth - 40 // padding
      
      // Calculate scale to fit container
      const scale = Math.min(
        containerWidth / this.baseImage.width,
        600 / this.baseImage.height // max height
      )
      
      this.canvas.width = this.baseImage.width * scale
      this.canvas.height = this.baseImage.height * scale
      
      // Store scale for coordinate conversion
      this.scale = scale
    },
    
    redrawCanvas() {
      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      
      // Draw base image
      this.ctx.drawImage(this.baseImage, 0, 0, this.canvas.width, this.canvas.height)
      
      // Draw all annotations
      this.annotations.forEach(annotation => {
        this.drawAnnotation(annotation)
      })
    },
    
    drawAnnotation(annotation) {
      const { type, data, color, size } = annotation
      
      this.ctx.save()
      
      switch (type) {
        case 'highlight':
          this.drawHighlight(data, color)
          break
        case 'draw':
          this.drawFreeform(data, color, size)
          break
        case 'rectangle':
          this.drawRectangle(data, color)
          break
        case 'circle':
          this.drawCircle(data, color)
          break
      }
      
      this.ctx.restore()
    },
    
    drawHighlight(data, color) {
      this.ctx.globalCompositeOperation = 'multiply'
      this.ctx.fillStyle = color
      this.ctx.globalAlpha = 0.3
      this.ctx.fillRect(data.x, data.y, data.width, data.height)
    },
    
    drawFreeform(data, color, size) {
      this.ctx.strokeStyle = color
      this.ctx.lineWidth = size
      this.ctx.lineCap = 'round'
      this.ctx.lineJoin = 'round'
      
      this.ctx.beginPath()
      data.forEach((point, index) => {
        if (index === 0) {
          this.ctx.moveTo(point.x, point.y)
        } else {
          this.ctx.lineTo(point.x, point.y)
        }
      })
      this.ctx.stroke()
    },
    
    drawRectangle(data, color) {
      this.ctx.strokeStyle = color
      this.ctx.lineWidth = 2
      this.ctx.strokeRect(data.x, data.y, data.width, data.height)
    },
    
    drawCircle(data, color) {
      this.ctx.strokeStyle = color
      this.ctx.lineWidth = 2
      this.ctx.beginPath()
      this.ctx.arc(data.centerX, data.centerY, data.radius, 0, 2 * Math.PI)
      this.ctx.stroke()
    },
    
    getMousePos(event) {
      const rect = this.canvas.getBoundingClientRect()
      return {
        x: (event.clientX - rect.left) * (this.canvas.width / rect.width),
        y: (event.clientY - rect.top) * (this.canvas.height / rect.height)
      }
    },
    
    startDrawing(event) {
      event.preventDefault()
      this.isDrawing = true
      
      const pos = this.getMousePos(event)
      this.startX = pos.x
      this.startY = pos.y
      
      if (this.activeTool === 'draw') {
        this.currentPath = [{ x: pos.x, y: pos.y }]
      }
    },
    
    draw(event) {
      if (!this.isDrawing) return
      
      event.preventDefault()
      const pos = this.getMousePos(event)
      
      if (this.activeTool === 'draw') {
        this.currentPath.push({ x: pos.x, y: pos.y })
        
        // Draw current stroke in real-time
        this.redrawCanvas()
        this.ctx.save()
        this.ctx.strokeStyle = this.activeColor
        this.ctx.lineWidth = this.brushSize
        this.ctx.lineCap = 'round'
        this.ctx.lineJoin = 'round'
        this.ctx.beginPath()
        this.currentPath.forEach((point, index) => {
          if (index === 0) {
            this.ctx.moveTo(point.x, point.y)
          } else {
            this.ctx.lineTo(point.x, point.y)
          }
        })
        this.ctx.stroke()
        this.ctx.restore()
      } else {
        // For shapes, show preview
        this.redrawCanvas()
        this.drawPreview(pos)
      }
    },
    
    drawPreview(currentPos) {
      this.ctx.save()
      this.ctx.strokeStyle = this.activeColor
      this.ctx.lineWidth = 2
      this.ctx.setLineDash([5, 5])
      
      switch (this.activeTool) {
        case 'highlight':
          this.ctx.globalCompositeOperation = 'multiply'
          this.ctx.fillStyle = this.activeColor
          this.ctx.globalAlpha = 0.3
          this.ctx.fillRect(
            this.startX, 
            this.startY, 
            currentPos.x - this.startX, 
            currentPos.y - this.startY
          )
          break
          
        case 'rectangle':
          this.ctx.strokeRect(
            this.startX, 
            this.startY, 
            currentPos.x - this.startX, 
            currentPos.y - this.startY
          )
          break
          
        case 'circle':
          const radius = Math.sqrt(
            Math.pow(currentPos.x - this.startX, 2) + 
            Math.pow(currentPos.y - this.startY, 2)
          )
          this.ctx.beginPath()
          this.ctx.arc(this.startX, this.startY, radius, 0, 2 * Math.PI)
          this.ctx.stroke()
          break
          
        case 'blur':
          this.ctx.strokeRect(
            this.startX, 
            this.startY, 
            currentPos.x - this.startX, 
            currentPos.y - this.startY
          )
          break
      }
      
      this.ctx.restore()
    },
    
    stopDrawing(event) {
      if (!this.isDrawing) return
      
      this.isDrawing = false
      
      const pos = this.getMousePos(event)
      
      // Create annotation based on tool
      let annotation = null
      
      switch (this.activeTool) {
        case 'highlight':
          annotation = {
            type: 'highlight',
            data: {
              x: Math.min(this.startX, pos.x),
              y: Math.min(this.startY, pos.y),
              width: Math.abs(pos.x - this.startX),
              height: Math.abs(pos.y - this.startY)
            },
            color: this.activeColor
          }
          break
          
        case 'draw':
          annotation = {
            type: 'draw',
            data: this.currentPath,
            color: this.activeColor,
            size: this.brushSize
          }
          break
          
        case 'rectangle':
          annotation = {
            type: 'rectangle',
            data: {
              x: Math.min(this.startX, pos.x),
              y: Math.min(this.startY, pos.y),
              width: Math.abs(pos.x - this.startX),
              height: Math.abs(pos.y - this.startY)
            },
            color: this.activeColor
          }
          break
          
        case 'circle':
          const radius = Math.sqrt(
            Math.pow(pos.x - this.startX, 2) + 
            Math.pow(pos.y - this.startY, 2)
          )
          annotation = {
            type: 'circle',
            data: {
              centerX: this.startX,
              centerY: this.startY,
              radius: radius
            },
            color: this.activeColor
          }
          break
          
        case 'blur':
          // Add blur area
          this.addBlurArea({
            x: Math.min(this.startX, pos.x),
            y: Math.min(this.startY, pos.y),
            width: Math.abs(pos.x - this.startX),
            height: Math.abs(pos.y - this.startY)
          })
          break
      }
      
      if (annotation) {
        this.annotations.push(annotation)
        this.saveState()
      }
      
      this.redrawCanvas()
      this.currentPath = null
    },
    
    addBlurArea(rect) {
      // The blur areas should be positioned relative to the canvas wrapper
      // Since both canvas and blur areas are in the same wrapper, we can use direct canvas coordinates
      const canvasRect = this.canvas.getBoundingClientRect()
      const canvasDisplayWidth = canvasRect.width
      const canvasDisplayHeight = canvasRect.height
      
      // Calculate scale factors between canvas internal size and display size
      const scaleX = canvasDisplayWidth / this.canvas.width
      const scaleY = canvasDisplayHeight / this.canvas.height
      
      // Convert canvas coordinates to display coordinates (relative to canvas wrapper)
      this.blurAreas.push({
        x: rect.x * scaleX,
        y: rect.y * scaleY,
        width: rect.width * scaleX,
        height: rect.height * scaleY
      })
      
      this.saveState()
    },
    
    getBlurStyle(blur) {
      return {
        position: 'absolute',
        left: blur.x + 'px',
        top: blur.y + 'px',
        width: blur.width + 'px',
        height: blur.height + 'px',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderRadius: '4px',
        pointerEvents: 'none',
        zIndex: 10
      }
    },
    
    handleTouch(event) {
      event.preventDefault()
      const touch = event.touches[0] || event.changedTouches[0]
      const mouseEvent = new MouseEvent(event.type.replace('touch', 'mouse'), {
        clientX: touch.clientX,
        clientY: touch.clientY
      })
      
      if (event.type === 'touchstart') {
        this.startDrawing(mouseEvent)
      } else if (event.type === 'touchmove') {
        this.draw(mouseEvent)
      } else if (event.type === 'touchend') {
        this.stopDrawing(mouseEvent)
      }
    },
    
    setActiveTool(toolId) {
      this.activeTool = toolId
      
      // Set default colors for different tools
      if (toolId === 'highlight') {
        this.activeColor = '#ffff00'
      } else if (toolId === 'draw') {
        this.activeColor = '#ff0000'
      } else {
        this.activeColor = '#ff0000'
      }
    },
    
    updateColor(event) {
      this.activeColor = event.target.value
    },
    
    updateBrushSize(event) {
      this.brushSize = parseInt(event.target.value)
    },
    
    saveState() {
      // Remove any history after current index
      this.history = this.history.slice(0, this.historyIndex + 1)
      
      // Save current state
      this.history.push({
        annotations: JSON.parse(JSON.stringify(this.annotations)),
        blurAreas: JSON.parse(JSON.stringify(this.blurAreas))
      })
      
      this.historyIndex = this.history.length - 1
      
      // Limit history size
      if (this.history.length > 20) {
        this.history.shift()
        this.historyIndex--
      }
      
      console.log(`💾 State saved: ${this.annotations.length} annotations, ${this.blurAreas.length} blur areas`)
    },
    
    undo() {
      if (this.historyIndex > 0) {
        this.historyIndex--
        const state = this.history[this.historyIndex]
        this.annotations = JSON.parse(JSON.stringify(state.annotations))
        this.blurAreas = JSON.parse(JSON.stringify(state.blurAreas))
        this.redrawCanvas()
      }
    },
    
    clearAll() {
      this.annotations = []
      this.blurAreas = []
      this.redrawCanvas()
      this.saveState()
    },
    
    async saveAnnotatedImage() {
      // Create a new canvas with the final image including blur areas
      const finalCanvas = document.createElement('canvas')
      finalCanvas.width = this.baseImage.width
      finalCanvas.height = this.baseImage.height
      
      const finalCtx = finalCanvas.getContext('2d')
      
      // Draw base image at full resolution
      finalCtx.drawImage(this.baseImage, 0, 0)
      
      // Scale annotations back to full resolution
      const scaleX = this.baseImage.width / this.canvas.width
      const scaleY = this.baseImage.height / this.canvas.height
      
      // Draw all annotations (highlights, drawings, shapes)
      this.annotations.forEach(annotation => {
        const scaledAnnotation = this.scaleAnnotation(annotation, scaleX, scaleY)
        this.drawAnnotationOnContext(finalCtx, scaledAnnotation)
      })
      
      // Draw blur areas as solid black rectangles on the final image
      this.blurAreas.forEach(blur => {
        // Convert blur area coordinates back to canvas coordinates, then scale to full resolution
        const canvasRect = this.canvas.getBoundingClientRect()
        const displayScaleX = canvasRect.width / this.canvas.width
        const displayScaleY = canvasRect.height / this.canvas.height
        
        // Convert blur coordinates back to canvas coordinates
        const canvasBlurX = blur.x / displayScaleX
        const canvasBlurY = blur.y / displayScaleY
        const canvasBlurWidth = blur.width / displayScaleX
        const canvasBlurHeight = blur.height / displayScaleY
        
        // Scale to full resolution
        const fullResX = canvasBlurX * scaleX
        const fullResY = canvasBlurY * scaleY
        const fullResWidth = canvasBlurWidth * scaleX
        const fullResHeight = canvasBlurHeight * scaleY
        
        // Draw solid black rectangle to hide sensitive information
        finalCtx.fillStyle = 'rgba(0, 0, 0, 0.9)'
        finalCtx.fillRect(fullResX, fullResY, fullResWidth, fullResHeight)
        
        // Add lock icon in center of blur area
        const centerX = fullResX + fullResWidth / 2
        const centerY = fullResY + fullResHeight / 2
        const iconSize = Math.min(fullResWidth, fullResHeight) * 0.3
        
        finalCtx.fillStyle = 'white'
        finalCtx.font = `${iconSize}px Arial`
        finalCtx.textAlign = 'center'
        finalCtx.textBaseline = 'middle'
        finalCtx.fillText('🔒', centerX, centerY)
      })
      
      // Convert to data URL
      const annotatedImageData = finalCanvas.toDataURL('image/jpeg', 0.9)
      
      this.$emit('save', {
        imageData: annotatedImageData,
        annotations: this.annotations,
        blurAreas: this.blurAreas
      })
    },
    
    scaleAnnotation(annotation, scaleX, scaleY) {
      const scaled = { ...annotation }
      
      switch (annotation.type) {
        case 'highlight':
        case 'rectangle':
          scaled.data = {
            x: annotation.data.x * scaleX,
            y: annotation.data.y * scaleY,
            width: annotation.data.width * scaleX,
            height: annotation.data.height * scaleY
          }
          break
          
        case 'circle':
          scaled.data = {
            centerX: annotation.data.centerX * scaleX,
            centerY: annotation.data.centerY * scaleY,
            radius: annotation.data.radius * Math.min(scaleX, scaleY)
          }
          break
          
        case 'draw':
          scaled.data = annotation.data.map(point => ({
            x: point.x * scaleX,
            y: point.y * scaleY
          }))
          scaled.size = annotation.size * Math.min(scaleX, scaleY)
          break
      }
      
      return scaled
    },
    
    drawAnnotationOnContext(ctx, annotation) {
      ctx.save()
      
      switch (annotation.type) {
        case 'highlight':
          ctx.globalCompositeOperation = 'multiply'
          ctx.fillStyle = annotation.color
          ctx.globalAlpha = 0.3
          ctx.fillRect(
            annotation.data.x, 
            annotation.data.y, 
            annotation.data.width, 
            annotation.data.height
          )
          break
          
        case 'draw':
          ctx.strokeStyle = annotation.color
          ctx.lineWidth = annotation.size
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          ctx.beginPath()
          annotation.data.forEach((point, index) => {
            if (index === 0) {
              ctx.moveTo(point.x, point.y)
            } else {
              ctx.lineTo(point.x, point.y)
            }
          })
          ctx.stroke()
          break
          
        case 'rectangle':
          ctx.strokeStyle = annotation.color
          ctx.lineWidth = 3
          ctx.strokeRect(
            annotation.data.x, 
            annotation.data.y, 
            annotation.data.width, 
            annotation.data.height
          )
          break
          
        case 'circle':
          ctx.strokeStyle = annotation.color
          ctx.lineWidth = 3
          ctx.beginPath()
          ctx.arc(
            annotation.data.centerX, 
            annotation.data.centerY, 
            annotation.data.radius, 
            0, 2 * Math.PI
          )
          ctx.stroke()
          break
      }
      
      ctx.restore()
    }
  }
}
</script>

<style scoped>
.image-annotation-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.annotation-toolbar {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;
  flex-wrap: wrap;
}

.tool-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.tool-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.tool-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.tool-btn.danger {
  color: #dc2626;
}

.tool-btn.danger:hover {
  background: #fef2f2;
  border-color: #dc2626;
}

.tool-btn.cancel {
  background: #6b7280;
  color: white;
  border-color: #6b7280;
}

.tool-btn.save {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.color-picker input[type="color"] {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.brush-size {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brush-size input[type="range"] {
  width: 80px;
}

.brush-size span {
  font-size: 12px;
  color: #6b7280;
  min-width: 30px;
}

.canvas-container {
  flex: 1;
  position: relative;
  padding: 20px;
  background: #f1f5f9;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.canvas-wrapper {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
}

canvas {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  height: auto;
  display: block;
  position: relative;
}

.blur-area {
  backdrop-filter: blur(8px);
  border: 2px dashed #ef4444;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.blur-area::after {
  content: '🔒 HIDDEN';
  color: white;
  font-size: 12px;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.instructions {
  padding: 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.instruction-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 14px;
}

@media (max-width: 768px) {
  .annotation-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .tool-group {
    justify-content: center;
  }
  
    .canvas-container {
      padding: 10px;
    }
    
    .canvas-wrapper {
      width: 100%;
    }
  }</style>